import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";

// This hook simplifies the process of dispatching a thunk and managing its loading state, result, and error state.
export function useThunk(thunk) {
  // State to manage the loading status of the thunk.
  const [isLoading, setIsLoading] = useState(false);
  // State to store the data returned by the thunk.
  const [data, setData] = useState(null);
  // State to store any error that occurs during the thunk execution.
  const [error, setError] = useState(null);
  // Redux's useDispatch hook to get the dispatch function.
  const dispatch = useDispatch();

  // Callback to run the thunk. It handles the loading state, error state, and result state.
  const runThunk = useCallback(
    async (arg) => {
      setIsLoading(true); // Set loading to true before starting the thunk.
      try {
        // Dispatch the thunk and wait for it to complete.
        // The 'unwrap' function is used to get the raw result or throw an error if the thunk was rejected.
        const res = await dispatch(thunk(arg)).unwrap();
        // Handle the successful result of the thunk.
        setData(res);
      } catch (err) {
        // Handle any errors that occurred during the thunk execution.
        setError(err);
      } finally {
        // Set loading to false after the thunk has completed (either successfully or with an error).
        setIsLoading(false);
      }
    },
    [dispatch, thunk] // Dependencies for useCallback to ensure it's stable and not recreated on each render.
  );

  // Return the thunk runner function, the loading state, the error state, and the data state.
  return [runThunk, isLoading, error, data];
}
